import React, {useEffect, useMemo, useRef, useState} from "react";
import {
  FaCheckSquare,
  FaChevronRight,
  FaEdit,
  FaEnvelopeOpenText,
  FaFileAlt,
  FaGoogle,
  FaImage,
  FaLayerGroup,
  FaLink,
  FaEye,
  FaClock,
  FaPaperPlane,
  FaPaperclip,
  FaPlusCircle,
  FaReply,
  FaSave,
  FaSignal,
  FaTimes,
  FaTrash,
  FaUpload,
  FaUsers,
} from "react-icons/fa";
import {ToastContainer, toast} from "react-toastify";
import {getDownloadURL, ref as storageRef, uploadBytesResumable} from "firebase/storage";
import "react-toastify/dist/ReactToastify.css";
import MailApnaArtistPreview from "../components/mailApnaArtist/MailApnaArtistPreview";
import "./mailApnaArtist.css";
import {storage} from "../firebase";
import {
  deleteMailApnaArtistLead,
  saveMailApnaArtistDraft,
  subscribeMailApnaArtistStudio,
  upsertMailApnaArtistAttachmentMeta,
  upsertMailApnaArtistLead,
  upsertMailApnaArtistTemplate,
} from "../shared/lib/mailApnaArtist/firestore";
import {
  applyMailMergeTokens,
  ensureMailApnaArtistComposer,
  ensureMailApnaArtistLead,
  ensureMailApnaArtistTemplate,
  isValidEmailAddress,
  MAIL_APNAARTIST_TABS,
  parseRecipientInput,
  sanitizeMailAttachmentName,
} from "../shared/types/mailApnaArtist";

const MAX_ATTACHMENT_BYTES = 20 * 1024 * 1024;
const APNAARTIST_CTA_URL = "https://apnaartist.com";
const ALLOWED_ATTACHMENT_EXTENSIONS = new Set([
  "pdf",
  "doc",
  "docx",
  "xls",
  "xlsx",
  "png",
  "jpg",
  "jpeg",
  "webp",
  "zip",
]);

function safeEmployee() {
  try {
    return JSON.parse(localStorage.getItem("employee") || "{}");
  } catch {
    return {};
  }
}

function formatDate(value) {
  const raw = value?.seconds ? value.seconds * 1000 : value;
  const date = raw ? new Date(raw) : null;
  return date && !Number.isNaN(date.getTime()) ? date.toLocaleString() : "Just now";
}

function formatDateTimeLocalInput(value) {
  const raw = value?.seconds ? value.seconds * 1000 : value;
  const date = raw ? new Date(raw) : null;
  if (!date || Number.isNaN(date.getTime())) return "";
  const pad = (input) => String(input).padStart(2, "0");
  return [
    date.getFullYear(),
    pad(date.getMonth() + 1),
    pad(date.getDate()),
  ].join("-") + `T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function formatNowAsDateTimeLocal() {
  return formatDateTimeLocalInput(Date.now() + (60 * 1000));
}

function formatBytes(value = 0) {
  const size = Number(value || 0);
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
}

function buildUniqueDeliveryTargets({selectedLeads = [], validToRecipients = []}) {
  const seenEmails = new Set();
  const sourceTargets = selectedLeads.length > 0 ?
    selectedLeads :
    validToRecipients.map((email, index) => ensureMailApnaArtistLead({
      id: `manual_${index}`,
      clientName: "",
      email,
      phone: "",
      location: "",
    }));

  return sourceTargets.filter((lead) => {
    const normalizedEmail = String(lead?.email || "").trim().toLowerCase();
    if (!normalizedEmail || seenEmails.has(normalizedEmail)) {
      return false;
    }
    seenEmails.add(normalizedEmail);
    return true;
  });
}

function statusClass(status) {
  const safe = String(status || "").toLowerCase();
  if (safe === "draft") return "mail-aa-status is-draft";
  if (safe === "ready") return "mail-aa-status is-ready";
  if (safe === "queued") return "mail-aa-status is-sending";
  if (safe === "connected") return "mail-aa-status is-connected";
  if (safe === "needs_reconnect") return "mail-aa-status is-reconnect";
  if (safe === "error" || safe === "failed") return "mail-aa-status is-error";
  if (safe === "scheduled") return "mail-aa-status is-ready";
  if (safe === "sent") return "mail-aa-status is-sent";
  if (safe === "sending") return "mail-aa-status is-sending";
  if (safe === "reply_pending") return "mail-aa-status is-reply";
  if (safe === "replied") return "mail-aa-status is-ready";
  if (safe === "seen") return "mail-aa-status is-seen";
  if (safe === "unseen" || safe === "not_seen") return "mail-aa-status is-unseen";
  return "mail-aa-status is-template";
}

function buildSenderOptions(accounts = []) {
  return accounts
    .flatMap((account) => {
      const aliases = Array.isArray(account?.senderAliases) ? account.senderAliases : [];
      return aliases
        .filter((alias) => alias?.email && alias?.sendReady === true)
        .filter((alias) => {
          const verificationStatus = String(alias.verificationStatus || "").trim().toLowerCase();
          return alias.isPrimary === true || !verificationStatus || verificationStatus === "accepted";
        })
        .map((alias) => ({
          value: String(alias.email || "").trim(),
          fromEmail: String(alias.email || "").trim(),
          fromDisplayName: String(alias.displayName || alias.email || "").trim(),
          label: `${alias.email || "-"} | ${alias.isPrimary ? "Primary" : "Verified SendAs"}`,
          aliasType: alias.isPrimary ? "Primary sender" : "Verified sender",
          accountId: String(account.id || "").trim(),
          connectedEmail: String(account.connectedEmail || account.primaryEmail || account.email || "").trim(),
          sendReady: alias.sendReady === true,
          replyToAddress: String(alias.replyToAddress || "").trim(),
          verificationStatus: String(alias.verificationStatus || "accepted").trim(),
        }));
    })
    .filter((item) => item.value);
}

function buildMessageComposer(message = {}) {
  const sender = message.sender || {};
  return ensureMailApnaArtistComposer({
    messageId: message.id || "",
    senderEmail: sender.fromEmail || sender.email || message.fromEmail || "",
    senderAccountId: sender.accountId || "",
    senderConnectedEmail: sender.connectedEmail || message.connectedEmail || "",
    senderDisplayName: sender.fromDisplayName || sender.name || message.fromDisplayName || "",
    senderAliasSource: sender.aliasSource || "",
    senderSendReady: sender.sendReady === true,
    senderReplyToAddress: sender.replyToAddress || "",
    sendMode: message.sendMode || (message.status === "scheduled" ? "scheduled" : "instant"),
    scheduledSendAt: formatDateTimeLocalInput(message.scheduledSendAt),
    scheduledTimezone: message.scheduledTimezone || Intl.DateTimeFormat().resolvedOptions().timeZone || "",
    status: message.status || "draft",
    statusNote: message.statusNote || "",
    toText: Array.isArray(message.to) ? message.to.join(", ") : Array.isArray(message.recipients) ? message.recipients.join(", ") : "",
    ccText: Array.isArray(message.cc) ? message.cc.join(", ") : "",
    bccText: Array.isArray(message.bcc) ? message.bcc.join(", ") : "",
    subject: message.subject || "",
    body: message.body || "",
    templateId: message.templateId || "",
    ctaLabel: message.ctaLabel || "Explore ApnaArtist",
    ctaUrl: APNAARTIST_CTA_URL,
    attachments: Array.isArray(message.attachments) ? message.attachments : [],
    attachmentNames: Array.isArray(message.attachmentNames) ? message.attachmentNames : [],
    bannerImageUrl: message.templateSnapshot?.bannerImageUrl || "",
    bannerAlt: message.templateSnapshot?.bannerAlt || "ApnaArtist outreach banner",
    replyTrackingState: message.replyTrackingState || "reply_pending",
    gmailMessageId: message.gmailMessageId || "",
    gmailThreadId: message.gmailThreadId || "",
    failedReason: message.failedReason || "",
  });
}

function SectionCard({title, copy, aside, children}) {
  return (
    <section className="mail-aa-sectionCard mail-aa-card">
      <div className="mail-aa-sectionHead">
        <div>
          <h2 className="mail-aa-sectionTitle">{title}</h2>
          {copy ? <p className="mail-aa-cardCopy">{copy}</p> : null}
        </div>
        {aside ? <div className="mail-aa-sectionAside">{aside}</div> : null}
      </div>
      <div className="mail-aa-sectionBody">
        {children}
      </div>
    </section>
  );
}

export default function MailApnaArtist() {
  const employee = useMemo(() => safeEmployee(), []);
  const scheduleDateInputRef = useRef(null);
  const sendNowGuardRef = useRef(false);
  const scheduleGuardRef = useRef(false);
  const [activeTab, setActiveTab] = useState("send");
  const [studio, setStudio] = useState({
    settings: [],
    senders: [],
    gmailAccounts: [],
    contacts: [],
    templates: [],
    messages: [],
    events: [],
    attachments: [],
  });
  const [composer, setComposer] = useState(() => ensureMailApnaArtistComposer({
    ctaUrl: APNAARTIST_CTA_URL,
  }));
  const [templateDraft, setTemplateDraft] = useState(ensureMailApnaArtistTemplate());
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const [savingDraft, setSavingDraft] = useState(false);
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [sendingMail, setSendingMail] = useState(false);
  const [schedulingMail, setSchedulingMail] = useState(false);
  const [schedulePickerRequested, setSchedulePickerRequested] = useState(false);
  const [savingLead, setSavingLead] = useState(false);
  const [showCcBcc, setShowCcBcc] = useState(false);
  const [actionNotice, setActionNotice] = useState({type: "", message: ""});
  const [attachmentUploadProgress, setAttachmentUploadProgress] = useState({});
  const [bannerUploadProgress, setBannerUploadProgress] = useState(0);
  const [bannerUploadStatus, setBannerUploadStatus] = useState("");
  const [attachmentUploadStatus, setAttachmentUploadStatus] = useState("");
  const [leadForm, setLeadForm] = useState(ensureMailApnaArtistLead());
  const [selectedLeadIds, setSelectedLeadIds] = useState([]);
  const [senderDropdownOpen, setSenderDropdownOpen] = useState(false);
  const [expandedMailId, setExpandedMailId] = useState("");
  const [replyDrafts, setReplyDrafts] = useState({});

  useEffect(() => {
    const unsubscribe = subscribeMailApnaArtistStudio({
      onData: (data) => setStudio(data),
      onError: (error) => {
        console.error("Mail ApnaArtist stream failed:", error);
        toast.error(error?.message || "Mail ApnaArtist Firestore stream failed.");
      },
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    function handleGmailConnected(event) {
      if (event.origin !== window.location.origin) return;
      if (event.data?.type !== "MAIL_APNAARTIST_GMAIL_CONNECTED") return;
      const email = String(event.data?.email || "").trim();
      toast.success(email ? `Gmail connected successfully: ${email}` : "Gmail connected successfully.");
      window.focus();
    }

    window.addEventListener("message", handleGmailConnected);
    return () => window.removeEventListener("message", handleGmailConnected);
  }, []);

  const connectedGmailAccounts = useMemo(
    () => (studio.gmailAccounts || []).slice().sort((a, b) => {
      const aTime = a.updatedAt?.seconds || a.connectedAt?.seconds || 0;
      const bTime = b.updatedAt?.seconds || b.connectedAt?.seconds || 0;
      return bTime - aTime;
    }),
    [studio.gmailAccounts],
  );

  const senderOptions = useMemo(
    () => buildSenderOptions(connectedGmailAccounts),
    [connectedGmailAccounts],
  );

  useEffect(() => {
    if (!selectedTemplateId && studio.templates[0]?.id) {
      setSelectedTemplateId(studio.templates[0].id);
    }
  }, [selectedTemplateId, studio.templates]);

  useEffect(() => {
    if (!selectedTemplateId) {
      setTemplateDraft((prev) => ensureMailApnaArtistTemplate({
        ...prev,
        id: "",
      }));
      return;
    }
    const nextTemplate = studio.templates.find((item) => item.id === selectedTemplateId);
    if (nextTemplate) {
      setTemplateDraft(ensureMailApnaArtistTemplate(nextTemplate));
      setComposer((prev) => ({...prev, templateId: nextTemplate.id}));
    }
  }, [selectedTemplateId, studio.templates]);

  useEffect(() => {
    if (senderOptions.length === 0 || composer.senderEmail) return;
    const nextOption = senderOptions[0];
    setComposer((prev) => ({
      ...prev,
      senderEmail: nextOption.fromEmail,
      senderAccountId: nextOption.accountId,
      senderConnectedEmail: nextOption.connectedEmail,
      senderDisplayName: nextOption.fromDisplayName,
      senderSendReady: nextOption.sendReady,
      senderReplyToAddress: nextOption.replyToAddress,
    }));
  }, [composer.senderEmail, senderOptions]);

  useEffect(() => {
    if (composer.sendMode !== "scheduled" || !schedulePickerRequested) return;
    const input = scheduleDateInputRef.current;
    if (!input) return;

    const timer = window.setTimeout(() => {
      input.scrollIntoView({behavior: "smooth", block: "center"});
      input.focus();
      if (!input.value && typeof input.showPicker === "function") {
        input.showPicker();
      }
      setSchedulePickerRequested(false);
    }, 80);

    return () => window.clearTimeout(timer);
  }, [composer.sendMode, schedulePickerRequested]);

  const contactSuggestions = useMemo(
    () => studio.contacts
      .slice(0, 8)
      .map((item) => ({
        id: item.id,
        label: item.clientName || item.name || item.fullName || item.email || item.phone || "Untitled contact",
        value: item.email || "",
      }))
      .filter((item) => item.value && isValidEmailAddress(item.value)),
    [studio.contacts],
  );

  const leads = useMemo(
    () => (studio.contacts || [])
      .map((item) => ensureMailApnaArtistLead({
        ...item,
        clientName: item.clientName || item.name || item.fullName || "",
      }))
      .filter((item) => item.email && isValidEmailAddress(item.email))
      .sort((a, b) => String(a.clientName || a.email).localeCompare(String(b.clientName || b.email))),
    [studio.contacts],
  );

  const selectedLeads = useMemo(
    () => leads.filter((lead) => selectedLeadIds.includes(lead.id)),
    [leads, selectedLeadIds],
  );

  useEffect(() => {
    if (selectedLeads.length === 1) {
      setComposer((prev) => ({
        ...prev,
        toText: selectedLeads[0].email || prev.toText,
      }));
    }
  }, [selectedLeads]);

  const activeTemplate = useMemo(
    () => ensureMailApnaArtistTemplate({
      ...templateDraft,
      bannerImageUrl: composer.bannerImageUrl || templateDraft.bannerImageUrl,
      bannerAlt: composer.bannerAlt || templateDraft.bannerAlt,
    }),
    [composer.bannerAlt, composer.bannerImageUrl, templateDraft],
  );

  const totalAttachmentBytes = useMemo(
    () => (composer.attachments || []).reduce((sum, item) => sum + Number(item?.size || 0), 0),
    [composer.attachments],
  );

  const hasUploadingAttachment = useMemo(
    () => Object.values(attachmentUploadProgress).some((value) => Number(value || 0) < 100),
    [attachmentUploadProgress],
  );

  const validToRecipients = useMemo(
    () => parseRecipientInput(composer.toText).filter((item) => isValidEmailAddress(item)),
    [composer.toText],
  );

  const selectedSenderOption = useMemo(
    () => senderOptions.find((item) => item.fromEmail === composer.senderEmail) || null,
    [composer.senderEmail, senderOptions],
  );

  const mailMergePreviewLead = useMemo(
    () => selectedLeads[0] || leads[0] || ensureMailApnaArtistLead(),
    [leads, selectedLeads],
  );

  const mergedBodyPreview = useMemo(
    () => applyMailMergeTokens(composer.body, mailMergePreviewLead),
    [composer.body, mailMergePreviewLead],
  );

  const mergedSubjectPreview = useMemo(
    () => applyMailMergeTokens(composer.subject, mailMergePreviewLead),
    [composer.subject, mailMergePreviewLead],
  );

  const uniqueDeliveryTargets = useMemo(
    () => buildUniqueDeliveryTargets({selectedLeads, validToRecipients}),
    [selectedLeads, validToRecipients],
  );
  const sendTargetsCount = uniqueDeliveryTargets.length;
  const baseDeliveryDisabled = !selectedSenderOption ||
    selectedSenderOption.sendReady !== true ||
    sendTargetsCount === 0 ||
    !composer.subject.trim() ||
    !composer.body.trim() ||
    hasUploadingAttachment;
  const sendDisabled = baseDeliveryDisabled ||
    composer.sendMode !== "instant" ||
    sendingMail;
  const scheduledDate = composer.scheduledSendAt ? new Date(composer.scheduledSendAt) : null;
  const hasValidScheduledDate = scheduledDate instanceof Date && !Number.isNaN(scheduledDate.getTime());
  const scheduleDisabled = baseDeliveryDisabled ||
    composer.sendMode !== "scheduled" ||
    schedulingMail;

  const metrics = useMemo(() => {
    const messages = studio.messages || [];
    const events = studio.events || [];
    const byStatus = (status) => messages.filter((item) => String(item.status || "").toLowerCase() === status).length;
    const byEvent = (eventType) => events.filter((item) => String(item.eventType || item.type || "").toLowerCase() === eventType).length;
    return {
      drafts: byStatus("draft"),
      sending: byStatus("sending") + byStatus("queued"),
      sent: byStatus("sent"),
      failed: byStatus("failed") + byEvent("failed"),
      replyPending: messages.filter((item) => String(item.replyTrackingState || "").toLowerCase() === "reply_pending").length,
      replied: messages.filter((item) => String(item.replyTrackingState || "").toLowerCase() === "replied").length,
    };
  }, [studio.events, studio.messages]);

  const manageCards = useMemo(
    () => (studio.messages || [])
      .slice()
      .sort((a, b) => {
        const aTime = a.updatedAt?.seconds || a.createdAt?.seconds || 0;
        const bTime = b.updatedAt?.seconds || b.createdAt?.seconds || 0;
        return bTime - aTime;
      }),
    [studio.messages],
  );

  const trackingCards = useMemo(
    () => manageCards.filter((item) => String(item.status || "").toLowerCase() === "sent" || item.gmailMessageId || item.sentAt),
    [manageCards],
  );

  const trackingStats = useMemo(() => {
    const seenCards = trackingCards.filter((item) =>
      Boolean(item.openedAt || item.firstOpenedAt || item.openTrackingState === "seen" || Number(item.openCount || 0) > 0)
    );
    const unseenCards = Math.max(0, trackingCards.length - seenCards.length);
    const totalOpens = trackingCards.reduce((sum, item) => sum + Number(item.openCount || 0), 0);
    return {
      sent: trackingCards.length,
      seen: seenCards.length,
      unseen: unseenCards,
      opens: totalOpens,
      latestSeenAt: seenCards
        .slice()
        .sort((a, b) => {
          const aTime = a.lastOpenedAt?.seconds || a.openedAt?.seconds || a.firstOpenedAt?.seconds || 0;
          const bTime = b.lastOpenedAt?.seconds || b.openedAt?.seconds || b.firstOpenedAt?.seconds || 0;
          return bTime - aTime;
        })[0]?.lastOpenedAt || seenCards[0]?.openedAt || null,
    };
  }, [trackingCards]);

  const headerStats = [
    {label: "Templates", value: studio.templates.length},
    {label: "Drafts", value: metrics.drafts},
    {label: "Sending", value: metrics.sending},
    {label: "Sent", value: metrics.sent},
  ];

  const monitorCards = [
    {label: "Sent", value: trackingStats.sent, icon: <FaPaperPlane />},
    {label: "Seen", value: trackingStats.seen, icon: <FaEye />},
    {label: "Not seen", value: trackingStats.unseen, icon: <FaSignal />},
    {label: "Opens", value: trackingStats.opens, icon: <FaEnvelopeOpenText />},
  ];

  function updateComposerSender(value) {
    const nextOption = senderOptions.find((item) => item.fromEmail === value);
    if (!nextOption) return;
    setSenderDropdownOpen(false);
    setComposer((prev) => ({
      ...prev,
      senderEmail: nextOption.fromEmail,
      senderAccountId: nextOption.accountId,
      senderConnectedEmail: nextOption.connectedEmail,
      senderDisplayName: nextOption.fromDisplayName,
      senderSendReady: nextOption.sendReady,
      senderReplyToAddress: nextOption.replyToAddress,
    }));
  }

  function appendRecipient(value) {
    setComposer((prev) => ({
      ...prev,
      toText: prev.toText ? `${prev.toText}, ${value}` : value,
    }));
  }

  function toggleLeadSelection(leadId) {
    setSelectedLeadIds((prev) => prev.includes(leadId) ?
      prev.filter((item) => item !== leadId) :
      [...prev, leadId]);
  }

  function toggleSelectAllLeads() {
    setSelectedLeadIds((prev) => prev.length === leads.length ? [] : leads.map((lead) => lead.id));
  }

  function loadLeadIntoForm(lead) {
    setLeadForm(ensureMailApnaArtistLead(lead));
    setActiveTab("leads");
  }

  async function handleSaveLead() {
    if (!leadForm.clientName.trim()) {
      toast.error("Client name is required.");
      return;
    }
    if (!isValidEmailAddress(leadForm.email)) {
      toast.error("A valid lead email is required.");
      return;
    }
    setSavingLead(true);
    try {
      const ref = await upsertMailApnaArtistLead(leadForm, employee);
      setLeadForm(ensureMailApnaArtistLead());
      setSelectedLeadIds((prev) => Array.from(new Set([...prev, ref.id])));
      toast.success("Lead saved successfully.");
    } catch (error) {
      console.error("Lead save failed:", error);
      toast.error(error?.message || "Failed to save lead.");
    } finally {
      setSavingLead(false);
    }
  }

  async function handleDeleteLead(leadId) {
    try {
      await deleteMailApnaArtistLead(leadId);
      setSelectedLeadIds((prev) => prev.filter((item) => item !== leadId));
      if (leadForm.id === leadId) {
        setLeadForm(ensureMailApnaArtistLead());
      }
      toast.success("Lead removed.");
    } catch (error) {
      console.error("Lead delete failed:", error);
      toast.error(error?.message || "Failed to delete lead.");
    }
  }

  function handleConnectGmail() {
    const connectUrl = "/api/mail-apnaartist/google/connect";
    const popup = window.open(connectUrl, "mailApnaArtistGmailConnect", "width=560,height=720");
    if (!popup) {
      window.location.href = connectUrl;
      return;
    }
    popup.focus();
  }

  function openSchedulePicker() {
    setSchedulePickerRequested(true);
  }

  async function handleSaveDraft(nextStatus = "draft", composerOverride = null, options = {}) {
    const nextComposerBase = composerOverride ? ensureMailApnaArtistComposer(composerOverride) : composer;
    const nextComposer = {
      ...nextComposerBase,
      ctaUrl: APNAARTIST_CTA_URL,
      scheduledTimezone: nextComposerBase.scheduledTimezone || Intl.DateTimeFormat().resolvedOptions().timeZone || "",
    };
    const nextRecipients = parseRecipientInput(nextComposer.toText).filter((item) => isValidEmailAddress(item));
    if (nextRecipients.length === 0) {
      toast.error("Add at least one valid To email before saving.");
      return null;
    }
    if (!nextComposer.subject.trim()) {
      toast.error("Subject is required before saving.");
      return null;
    }
    if (!nextComposer.body.trim()) {
      toast.error("Body is required before saving.");
      return null;
    }

    setSavingDraft(true);
    try {
      const ref = await saveMailApnaArtistDraft({
        composer: {...nextComposer, status: nextStatus},
        template: activeTemplate,
        employee,
      });
      if (!composerOverride) {
        setComposer((prev) => ({
          ...prev,
          messageId: ref.id,
          status: nextStatus,
          scheduledTimezone: nextComposer.scheduledTimezone,
        }));
      }
      if (options.toast !== false) {
        toast.success(nextStatus === "draft" ? "Draft saved successfully." : "Send-ready draft updated.");
      }
      if (options.notice !== false) {
        setActionNotice({
          type: "success",
          message: nextStatus === "scheduled" ? "Mail scheduled successfully." : "Draft saved successfully.",
        });
      }
      return ref.id;
    } catch (error) {
      console.error("Mail draft save failed:", error);
      if (options.toast !== false) {
        toast.error(error?.message || "Failed to save draft.");
      }
      if (options.notice !== false) {
        setActionNotice({
          type: "error",
          message: error?.message || "Failed to save draft.",
        });
      }
      return null;
    } finally {
      setSavingDraft(false);
    }
  }

  async function handleSaveTemplate() {
    if (!templateDraft.templateName.trim()) {
      toast.error("Template name is required.");
      return;
    }
    setSavingTemplate(true);
    try {
      const ref = await upsertMailApnaArtistTemplate(templateDraft, employee);
      setSelectedTemplateId(ref.id);
      setTemplateDraft((prev) => ({...prev, id: ref.id}));
      setComposer((prev) => ({...prev, templateId: ref.id}));
      toast.success("Template saved successfully.");
    } catch (error) {
      console.error("Template save failed:", error);
      toast.error(error?.message || "Failed to save template.");
    } finally {
      setSavingTemplate(false);
    }
  }

  async function uploadFileToStorage(file, folder, onProgress) {
    const cleanName = sanitizeMailAttachmentName(file.name || "asset");
    const path = `mail_apnaartist/${folder}/${Date.now()}_${cleanName}`;
    const uploadRef = storageRef(storage, path);
    const task = uploadBytesResumable(uploadRef, file, {
      contentType: file.type || "application/octet-stream",
    });

    await new Promise((resolve, reject) => {
      task.on("state_changed", (snapshot) => {
        const progress = snapshot.totalBytes > 0 ?
          Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100) :
          0;
        onProgress?.(progress);
      }, reject, resolve);
    });

    const downloadUrl = await getDownloadURL(task.snapshot.ref);
    return {
      storagePath: path,
      downloadUrl,
      storageBucket: storage.app.options.storageBucket || "",
    };
  }

  async function handleAttachmentFiles(event) {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;

    for (const file of files) {
      const ext = String(file.name || "").split(".").pop().toLowerCase();
      if (!ALLOWED_ATTACHMENT_EXTENSIONS.has(ext)) {
        setAttachmentUploadStatus("failed");
        toast.error(`${file.name} is not an allowed attachment type.`);
        continue;
      }
      if ((totalAttachmentBytes + file.size) > MAX_ATTACHMENT_BYTES) {
        setAttachmentUploadStatus("failed");
        toast.error("Attachments cannot exceed 20 MB total.");
        continue;
      }

      const tempId = `${Date.now()}_${Math.random().toString(16).slice(2)}`;
      setAttachmentUploadStatus("uploading");
      setAttachmentUploadProgress((prev) => ({...prev, [tempId]: 0}));

      try {
        const upload = await uploadFileToStorage(file, "attachments", (progress) => {
          setAttachmentUploadProgress((prev) => ({...prev, [tempId]: progress}));
        });
        const attachmentDoc = {
          id: tempId,
          fileName: sanitizeMailAttachmentName(file.name),
          size: file.size,
          contentType: file.type || "application/octet-stream",
          storagePath: upload.storagePath,
          downloadUrl: upload.downloadUrl,
          storageBucket: upload.storageBucket,
          status: "ready",
        };
        const ref = await upsertMailApnaArtistAttachmentMeta(attachmentDoc, employee);
        setComposer((prev) => {
          const attachments = [...(prev.attachments || []), {...attachmentDoc, id: ref.id}];
          return {
            ...prev,
            attachments,
            attachmentNames: attachments.map((item) => item.fileName),
          };
        });
        setAttachmentUploadStatus("uploaded");
      } catch (error) {
        console.error("Attachment upload failed:", error);
        setAttachmentUploadStatus("failed");
        toast.error(error?.message || `Failed to upload ${file.name}.`);
      } finally {
        setAttachmentUploadProgress((prev) => ({...prev, [tempId]: 100}));
      }
    }

    event.target.value = "";
  }

  async function handleBannerUpload(event) {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!["png", "jpg", "jpeg", "webp"].includes(String(file.name || "").split(".").pop().toLowerCase())) {
      setBannerUploadStatus("failed");
      toast.error("Banner must be PNG, JPG, or WebP.");
      return;
    }
    try {
      setBannerUploadStatus("uploading");
      setBannerUploadProgress(1);
      const upload = await uploadFileToStorage(file, "banners", setBannerUploadProgress);
      setTemplateDraft((prev) => ({
        ...prev,
        bannerImageUrl: upload.downloadUrl,
        bannerAlt: prev.bannerAlt || sanitizeMailAttachmentName(file.name),
      }));
      setComposer((prev) => ({
        ...prev,
        bannerImageUrl: upload.downloadUrl,
        bannerAlt: prev.bannerAlt || sanitizeMailAttachmentName(file.name),
      }));
      setBannerUploadStatus("uploaded");
      toast.success("Banner uploaded successfully.");
    } catch (error) {
      console.error("Banner upload failed:", error);
      setBannerUploadStatus("failed");
      toast.error(error?.message || "Failed to upload banner.");
    } finally {
      setBannerUploadProgress(100);
      event.target.value = "";
    }
  }

  async function handleTemplateLogoUpload(event) {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!["png", "jpg", "jpeg", "webp"].includes(String(file.name || "").split(".").pop().toLowerCase())) {
      toast.error("Logo must be PNG, JPG, or WebP.");
      return;
    }
    try {
      const upload = await uploadFileToStorage(file, "logos");
      setTemplateDraft((prev) => ({
        ...prev,
        logoUrl: upload.downloadUrl,
      }));
      toast.success("Template logo uploaded successfully.");
    } catch (error) {
      console.error("Logo upload failed:", error);
      toast.error(error?.message || "Failed to upload logo.");
    } finally {
      event.target.value = "";
    }
  }

  function removeAttachment(attachmentId) {
    setComposer((prev) => {
      const attachments = (prev.attachments || []).filter((item) => item.id !== attachmentId);
      return {
        ...prev,
        attachments,
        attachmentNames: attachments.map((item) => item.fileName),
      };
    });
  }

  async function handleSendNow() {
    if (sendDisabled || sendNowGuardRef.current) return;
    setActionNotice({type: "", message: ""});
    sendNowGuardRef.current = true;
    setSendingMail(true);
    try {
      const targets = uniqueDeliveryTargets;
      let queuedCount = 0;

      for (const lead of targets) {
        const personalizedComposer = {
          ...composer,
          toText: lead.email,
          subject: applyMailMergeTokens(composer.subject, lead),
          body: applyMailMergeTokens(composer.body, lead),
          ctaUrl: APNAARTIST_CTA_URL,
          messageId: "",
          sendMode: "instant",
          scheduledSendAt: "",
          scheduledTimezone: "",
          failedReason: "",
        };
        const savedMessageId = await handleSaveDraft("queued", personalizedComposer, {toast: false, notice: false});
        if (!savedMessageId) {
          throw new Error(`Failed to queue mail for ${lead.clientName || lead.email}.`);
        }
        queuedCount += 1;
        if (targets.length === 1) {
          setComposer((prev) => ({
            ...prev,
            messageId: savedMessageId,
            toText: lead.email,
            subject: personalizedComposer.subject,
            body: personalizedComposer.body,
            status: "queued",
            gmailMessageId: "",
            gmailThreadId: "",
            replyTrackingState: "reply_pending",
            failedReason: "",
          }));
        }
      }

      if (targets.length > 1) {
        setComposer((prev) => ({
          ...prev,
          status: "queued",
          replyTrackingState: "reply_pending",
          failedReason: "",
        }));
      }
      setActionNotice({
        type: "success",
        message: targets.length > 1 ?
          `${queuedCount} emails queued. Backend sender will deliver them automatically.` :
          "Mail queued. Backend sender will deliver it automatically.",
      });
      toast.success(targets.length > 1 ?
        `${queuedCount} personalized emails queued successfully.` :
        "Mail queued successfully.");
    } catch (error) {
      console.error("Mail send failed:", error);
      setComposer((prev) => ({
        ...prev,
        status: "failed",
        failedReason: error?.message || "Mail send failed.",
      }));
      setActionNotice({
        type: "error",
        message: error?.message || "Mail send failed.",
      });
      toast.error(error?.message || "Mail send failed.");
    } finally {
      setSendingMail(false);
      sendNowGuardRef.current = false;
    }
  }

  async function handleScheduleMail() {
    if (scheduleDisabled || scheduleGuardRef.current) return;
    setActionNotice({type: "", message: ""});
    if (!hasValidScheduledDate) {
      toast.info("Select the scheduled date and time first.");
      setActionNotice({
        type: "error",
        message: "Select the scheduled date and time first.",
      });
      openSchedulePicker();
      return;
    }
    if (scheduledDate.getTime() <= Date.now()) {
      toast.error("Scheduled time must be in the future.");
      setActionNotice({
        type: "error",
        message: "Scheduled time must be in the future.",
      });
      openSchedulePicker();
      return;
    }
    scheduleGuardRef.current = true;
    setSchedulingMail(true);
    try {
      const targets = uniqueDeliveryTargets;
      let scheduledCount = 0;

      for (const lead of targets) {
        const personalizedComposer = {
          ...composer,
          toText: lead.email,
          subject: applyMailMergeTokens(composer.subject, lead),
          body: applyMailMergeTokens(composer.body, lead),
          ctaUrl: APNAARTIST_CTA_URL,
          messageId: "",
          sendMode: "scheduled",
          scheduledTimezone: composer.scheduledTimezone || Intl.DateTimeFormat().resolvedOptions().timeZone || "",
          failedReason: "",
        };
        const savedMessageId = await handleSaveDraft("scheduled", personalizedComposer, {toast: false});
        if (!savedMessageId) {
          throw new Error(`Failed to schedule mail for ${lead.clientName || lead.email}.`);
        }
        scheduledCount += 1;
        if (targets.length === 1) {
          setComposer((prev) => ({
            ...prev,
            messageId: savedMessageId,
            toText: lead.email,
            subject: personalizedComposer.subject,
            body: personalizedComposer.body,
            status: "scheduled",
            sendMode: "scheduled",
            failedReason: "",
          }));
        }
      }

      if (targets.length > 1) {
        setComposer((prev) => ({
          ...prev,
          status: "scheduled",
          sendMode: "scheduled",
          failedReason: "",
        }));
      }

      setActionNotice({
        type: "success",
        message: targets.length > 1 ?
          `${scheduledCount} emails scheduled successfully.` :
          "Email scheduled successfully.",
      });
      toast.success(
          targets.length > 1 ?
            `${scheduledCount} personalized emails scheduled successfully.` :
            "Email scheduled successfully.",
      );
    } catch (error) {
      console.error("Mail schedule failed:", error);
      setComposer((prev) => ({
        ...prev,
        status: "failed",
        failedReason: error?.message || "Mail schedule failed.",
      }));
      setActionNotice({
        type: "error",
        message: error?.message || "Mail schedule failed.",
      });
      toast.error(error?.message || "Mail schedule failed.");
    } finally {
      setSchedulingMail(false);
      scheduleGuardRef.current = false;
    }
  }

  function loadMessageToComposer(message) {
    setComposer(buildMessageComposer(message));
    setTemplateDraft(ensureMailApnaArtistTemplate(message.templateSnapshot || {}));
    setSelectedTemplateId(String(message.templateId || ""));
    setActiveTab("send");
  }

  function getMailReplies(message = {}) {
    const arrays = [
      message.replies,
      message.clientReplies,
      message.replyMessages,
      message.gmailReplies,
    ];
    return arrays
      .find((value) => Array.isArray(value))
      ?.map((reply, index) => ({
        id: String(reply.id || reply.gmailMessageId || `reply_${index}`),
        from: String(reply.from || reply.fromEmail || reply.sender || "Client").trim(),
        body: String(reply.body || reply.text || reply.snippet || reply.message || "").trim(),
        createdAt: reply.createdAt || reply.receivedAt || reply.date || null,
      }))
      .filter((reply) => reply.body || reply.from) || [];
  }

  function prepareReply(message = {}) {
    const replyText = String(replyDrafts[message.id] || "").trim();
    if (!replyText) {
      toast.error("Write a reply first.");
      return;
    }
    const to = (message.to || message.recipients || [])[0] || "";
    setComposer((prev) => ({
      ...prev,
      messageId: "",
      senderEmail: message.sender?.fromEmail || message.fromEmail || prev.senderEmail,
      senderAccountId: message.sender?.accountId || prev.senderAccountId,
      senderConnectedEmail: message.sender?.connectedEmail || message.connectedEmail || prev.senderConnectedEmail,
      senderDisplayName: message.sender?.fromDisplayName || message.fromDisplayName || prev.senderDisplayName,
      senderReplyToAddress: message.sender?.replyToAddress || prev.senderReplyToAddress,
      toText: to,
      ccText: "",
      bccText: "",
      subject: String(message.subject || "").toLowerCase().startsWith("re:") ? message.subject : `Re: ${message.subject || "ApnaArtist mail"}`,
      body: replyText,
      gmailThreadId: message.gmailThreadId || "",
      status: "draft",
      replyTrackingState: "reply_pending",
      failedReason: "",
    }));
    setReplyDrafts((prev) => ({...prev, [message.id]: ""}));
    setActiveTab("send");
    toast.success("Reply loaded in composer.");
  }

  function renderSendTab() {
    return (
      <div className="mail-aa-grid">
        <div className="mail-aa-stack">
          <SectionCard title="Compose Mail" copy="Premium Gmail-ready composer for polished outreach, personalized leads, and send-safe delivery.">
            <div className="mail-aa-formGrid">
              <div className={`mail-aa-field mail-aa-fieldSpan2 mail-aa-composeTile mail-aa-composeTileHero mail-aa-composeTileDropdownHost ${senderDropdownOpen ? "is-dropdown-open" : ""}`}>
                <div className="mail-aa-composeTileHead">
                  <span className="mail-aa-composeIcon"><FaGoogle /></span>
                  <div>
                    <span className="mail-aa-label">From</span>
                    <div className="mail-aa-help">Choose a verified Gmail sender for this message.</div>
                  </div>
                </div>
                <div className={`mail-aa-senderDropdown ${senderDropdownOpen ? "is-open" : ""}`}>
                  <button
                    type="button"
                    className={`mail-aa-senderTrigger ${selectedSenderOption ? "has-value" : ""}`}
                    onClick={() => setSenderDropdownOpen((prev) => !prev)}
                  >
                    <span className="mail-aa-senderIcon"><FaGoogle /></span>
                    <span>
                      <strong>{selectedSenderOption?.fromEmail || "Select a verified Gmail sender"}</strong>
                      <small>{selectedSenderOption?.aliasType || "Choose the sender for this mail"}</small>
                    </span>
                    <FaChevronRight className="mail-aa-senderChevron" />
                  </button>
                  {senderDropdownOpen ? (
                    <div className="mail-aa-senderMenu">
                      {senderOptions.length > 0 ? senderOptions.map((item) => {
                        const selected = item.fromEmail === composer.senderEmail;
                        return (
                          <button
                            key={`${item.accountId}:${item.fromEmail}`}
                            type="button"
                            className={`mail-aa-senderOption ${selected ? "is-selected" : ""}`}
                            onClick={() => updateComposerSender(item.fromEmail)}
                          >
                            <span className="mail-aa-senderIcon"><FaEnvelopeOpenText /></span>
                            <span>
                              <strong>{item.fromEmail}</strong>
                              <small>{item.aliasType}</small>
                            </span>
                            <span className={statusClass("ready")}>{selected ? "Selected" : "Send ready"}</span>
                          </button>
                        );
                      }) : (
                        <div className="mail-aa-senderEmpty">
                          <FaGoogle /> No send-ready Gmail sender found. Use Reconnect Gmail below.
                        </div>
                      )}
                    </div>
                  ) : null}
                </div>
              </div>

              <label className="mail-aa-field mail-aa-composeTile">
                <div className="mail-aa-composeTileHead">
                  <span className="mail-aa-composeIcon"><FaFileAlt /></span>
                  <span className="mail-aa-label">Template</span>
                </div>
                <select
                  className="mail-aa-select"
                  value={selectedTemplateId}
                  onChange={(event) => setSelectedTemplateId(event.target.value)}
                >
                  <option value="">Use current design draft</option>
                  {studio.templates.map((item) => (
                    <option key={item.id} value={item.id}>{item.templateName || "Untitled template"}</option>
                  ))}
                </select>
              </label>

              <div className="mail-aa-card mail-aa-inlinePanel mail-aa-composeTile">
                <span className="mail-aa-composeIcon"><FaSignal /></span>
                <div>
                  <div className="mail-aa-inlineTitle">Status</div>
                  <div className="mail-aa-help">Live composer state from real mail data.</div>
                </div>
                <span className={statusClass(composer.status)}>{String(composer.status || "draft").replace(/_/g, " ")}</span>
              </div>
            </div>

            <div className="mail-aa-card mail-aa-inlinePanel mail-aa-composeTile mail-aa-leadTargetTile">
              <span className="mail-aa-composeIcon"><FaUsers /></span>
              <div>
                <div className="mail-aa-inlineTitle">Lead targeting</div>
                <div className="mail-aa-help">
                  {selectedLeads.length > 0 ?
                    `${selectedLeads.length} lead${selectedLeads.length > 1 ? "s selected" : " selected"} for personalized sending.` :
                    "No lead selected yet. You can still send to manual To emails."}
                </div>
              </div>
              <button type="button" className="mail-aa-btn mail-aa-btnSecondary" onClick={() => setActiveTab("leads")}>
                <FaUsers /> Manage Leads
              </button>
            </div>

            <div className="mail-aa-recipientCard mail-aa-composeTile">
              <div className="mail-aa-recipientTop">
                <div className="mail-aa-composeTileHead">
                  <span className="mail-aa-composeIcon"><FaEnvelopeOpenText /></span>
                  <span className="mail-aa-label">Recipients</span>
                </div>
                <button type="button" className="mail-aa-linkBtn" onClick={() => setShowCcBcc((prev) => !prev)}>
                  {showCcBcc ? "Hide Cc/Bcc" : "Add Cc/Bcc"}
                </button>
              </div>
              <label className="mail-aa-field">
                <input
                  className="mail-aa-input"
                  value={composer.toText}
                  onChange={(event) => setComposer((prev) => ({...prev, toText: event.target.value}))}
                  placeholder="To"
                />
              </label>
              {showCcBcc ? (
                <div className="mail-aa-formSplit">
                  <label className="mail-aa-field">
                    <input
                      className="mail-aa-input"
                      value={composer.ccText}
                      onChange={(event) => setComposer((prev) => ({...prev, ccText: event.target.value}))}
                      placeholder="Cc"
                    />
                  </label>
                  <label className="mail-aa-field">
                    <input
                      className="mail-aa-input"
                      value={composer.bccText}
                      onChange={(event) => setComposer((prev) => ({...prev, bccText: event.target.value}))}
                      placeholder="Bcc"
                    />
                  </label>
                </div>
              ) : null}
              <div className="mail-aa-optionRow">
                {contactSuggestions.length > 0 ? contactSuggestions.map((item) => (
                  <button key={item.id} type="button" className="mail-aa-badge" onClick={() => appendRecipient(item.value)}>
                    <FaUsers /> {item.label}
                  </button>
                )) : (
                  <span className="mail-aa-badge">No saved email contacts yet</span>
                )}
              </div>
            </div>

            <div className="mail-aa-recipientCard mail-aa-composeTile">
              <div>
                <div className="mail-aa-composeTileHead">
                  <span className="mail-aa-composeIcon"><FaCheckSquare /></span>
                  <div className="mail-aa-inlineTitle">Lead Selection</div>
                </div>
                <div className="mail-aa-help">Pick one lead or select all. Each selected lead will receive a separate personalized email with {"{{1}}"} replaced by the client name.</div>
              </div>
              <div className="mail-aa-actions">
                <button type="button" className="mail-aa-btn mail-aa-btnSecondary" onClick={toggleSelectAllLeads} disabled={leads.length === 0}>
                  <FaCheckSquare /> {selectedLeadIds.length === leads.length && leads.length > 0 ? "Clear All Leads" : "Select All Leads"}
                </button>
                <span className="mail-aa-help">{leads.length} total leads available</span>
              </div>
              {leads.length > 0 ? (
                <div className="mail-aa-list">
                  {leads.map((lead) => {
                    const selected = selectedLeadIds.includes(lead.id);
                    return (
                      <button
                        key={lead.id}
                        type="button"
                        className={`mail-aa-leadRow ${selected ? "is-selected" : ""}`}
                        onClick={() => toggleLeadSelection(lead.id)}
                      >
                        <div>
                          <div className="mail-aa-listTitle">{lead.clientName || "Unnamed lead"}</div>
                          <div className="mail-aa-help">{lead.email} {lead.location ? ` | ${lead.location}` : ""}</div>
                        </div>
                        <span className={statusClass(selected ? "ready" : "template")}>{selected ? "Selected" : "Available"}</span>
                      </button>
                    );
                  })}
                </div>
              ) : (
                <div className="mail-aa-empty mail-aa-emptyInline">
                  <div className="mail-aa-emptyIcon"><FaUsers /></div>
                  <p className="mail-aa-emptyCopy">No leads saved yet. Add them in the Leads tab.</p>
                </div>
              )}
            </div>

            <label className="mail-aa-field mail-aa-composeTile">
              <div className="mail-aa-composeTileHead">
                <span className="mail-aa-composeIcon"><FaEdit /></span>
                <span className="mail-aa-label">Subject</span>
              </div>
              <input
                className="mail-aa-input"
                value={composer.subject}
                onChange={(event) => setComposer((prev) => ({...prev, subject: event.target.value}))}
                placeholder="Write a polished subject line"
              />
            </label>

            <div className="mail-aa-editor mail-aa-glass">
              <div className="mail-aa-editorHead">
                <div className="mail-aa-composeTileHead">
                  <span className="mail-aa-composeIcon"><FaEnvelopeOpenText /></span>
                  <div>
                  <h3 className="mail-aa-cardTitle">Message Body</h3>
                  <div className="mail-aa-help">A clean text-first composer with a premium HTML template preview beside it.</div>
                  </div>
                </div>
                <div className="mail-aa-toolbar">
                  <span className="mail-aa-tool"><FaEnvelopeOpenText /></span>
                  <span className="mail-aa-tool"><FaLink /></span>
                  <span className="mail-aa-tool"><FaImage /></span>
                </div>
              </div>
              <textarea
                className="mail-aa-textarea mail-aa-textareaTall"
                value={composer.body}
                onChange={(event) => setComposer((prev) => ({...prev, body: event.target.value}))}
                placeholder="Write the email body"
              />
            </div>

            <div className="mail-aa-formSplit">
              <label className="mail-aa-field mail-aa-composeTile">
                <div className="mail-aa-composeTileHead">
                  <span className="mail-aa-composeIcon"><FaPaperPlane /></span>
                  <span className="mail-aa-label">CTA text</span>
                </div>
                <input
                  className="mail-aa-input"
                  value={composer.ctaLabel}
                  onChange={(event) => setComposer((prev) => ({...prev, ctaLabel: event.target.value}))}
                  placeholder="Explore ApnaArtist"
                />
              </label>
              <label className="mail-aa-field mail-aa-composeTile">
                <div className="mail-aa-composeTileHead">
                  <span className="mail-aa-composeIcon"><FaLink /></span>
                  <span className="mail-aa-label">CTA URL</span>
                </div>
                <input
                  className="mail-aa-input"
                  value={composer.ctaUrl}
                  onChange={() => setComposer((prev) => ({...prev, ctaUrl: APNAARTIST_CTA_URL}))}
                  placeholder={APNAARTIST_CTA_URL}
                  readOnly
                />
              </label>
            </div>

            <div className="mail-aa-recipientCard mail-aa-composeTile">
              <div>
                <div className="mail-aa-composeTileHead">
                  <span className="mail-aa-composeIcon"><FaClock /></span>
                  <div className="mail-aa-inlineTitle">Delivery Mode</div>
                </div>
                <div className="mail-aa-help">Choose instant send or save this message for automatic delivery at a scheduled date and time.</div>
              </div>
              <div className="mail-aa-modeRow">
                <button
                  type="button"
                  className={`mail-aa-modeBtn ${composer.sendMode === "instant" ? "is-active" : ""}`}
                  onClick={() => setComposer((prev) => ({
                    ...prev,
                    sendMode: "instant",
                    status: prev.status === "scheduled" ? "draft" : prev.status,
                  }))}
                >
                  <FaPaperPlane /> Send Mail Now
                </button>
                <button
                  type="button"
                  className={`mail-aa-modeBtn ${composer.sendMode === "scheduled" ? "is-active" : ""}`}
                  onClick={() => {
                    setComposer((prev) => ({
                      ...prev,
                      sendMode: "scheduled",
                      scheduledTimezone: prev.scheduledTimezone || Intl.DateTimeFormat().resolvedOptions().timeZone || "",
                    }));
                    openSchedulePicker();
                  }}
                >
                  <FaClock /> Schedule Mail
                </button>
              </div>
              {composer.sendMode === "scheduled" ? (
                <div className="mail-aa-formSplit">
                  <label className="mail-aa-field">
                    <span className="mail-aa-label">Scheduled date and time</span>
                    <input
                      ref={scheduleDateInputRef}
                      type="datetime-local"
                      className="mail-aa-input"
                      min={formatNowAsDateTimeLocal()}
                      value={composer.scheduledSendAt}
                      onChange={(event) => setComposer((prev) => ({
                        ...prev,
                        scheduledSendAt: event.target.value,
                        scheduledTimezone: prev.scheduledTimezone || Intl.DateTimeFormat().resolvedOptions().timeZone || "",
                      }))}
                    />
                  </label>
                  <label className="mail-aa-field">
                    <span className="mail-aa-label">Timezone</span>
                    <input
                      className="mail-aa-input"
                      value={composer.scheduledTimezone || Intl.DateTimeFormat().resolvedOptions().timeZone || ""}
                      placeholder="Asia/Kolkata"
                      readOnly
                    />
                  </label>
                </div>
              ) : null}
              {composer.sendMode === "scheduled" ? (
                <div className="mail-aa-help">
                  {hasValidScheduledDate && scheduledDate.getTime() > Date.now() ?
                    `Scheduled for ${scheduledDate.toLocaleString()} (${composer.scheduledTimezone || Intl.DateTimeFormat().resolvedOptions().timeZone || "Local time"})` :
                    "Choose a future date and time, then click the Schedule Mail action below."}
                </div>
              ) : null}
            </div>
          </SectionCard>

          <SectionCard title="Banner And Attachments">
            <div className="mail-aa-uploadCards">
              <label className="mail-aa-uploadCard">
                <span className="mail-aa-uploadCardIcon"><FaImage /></span>
                <span className="mail-aa-uploadCardText">
                  <strong>{activeTemplate.bannerImageUrl ? "Banner ready" : "Upload banner"}</strong>
                  <small>PNG, JPG, or WebP</small>
                </span>
                <span className={statusClass(bannerUploadStatus === "failed" ? "error" : activeTemplate.bannerImageUrl ? "ready" : "template")}>
                  {bannerUploadStatus === "uploading" ? `${bannerUploadProgress}%` : bannerUploadStatus === "failed" ? "Failed" : activeTemplate.bannerImageUrl ? "Uploaded" : "Choose"}
                </span>
                <input type="file" accept=".png,.jpg,.jpeg,.webp" hidden onChange={handleBannerUpload} />
              </label>

              <label className="mail-aa-uploadCard">
                <span className="mail-aa-uploadCardIcon"><FaPaperclip /></span>
                <span className="mail-aa-uploadCardText">
                  <strong>{composer.attachments.length ? `${composer.attachments.length} attachment${composer.attachments.length > 1 ? "s" : ""}` : "Add attachment"}</strong>
                  <small>PDF, docs, sheets, images, ZIP</small>
                </span>
                <span className={statusClass(attachmentUploadStatus === "failed" ? "error" : composer.attachments.length ? "ready" : "template")}>
                  {attachmentUploadStatus === "uploading" ? "Uploading" : attachmentUploadStatus === "failed" ? "Failed" : composer.attachments.length ? "Uploaded" : "Choose"}
                </span>
                <input type="file" hidden multiple onChange={handleAttachmentFiles} />
              </label>
            </div>

            <div className="mail-aa-uploadStatusGrid">
              <div className={`mail-aa-miniStatus ${bannerUploadStatus || activeTemplate.bannerImageUrl ? "is-visible" : ""}`}>
                {bannerUploadStatus === "uploading" ? (
                  <>
                    <span className="mail-aa-miniBar"><span style={{width: `${bannerUploadProgress}%`}} /></span>
                    Banner uploading {bannerUploadProgress}%
                  </>
                ) : bannerUploadStatus === "failed" ? "Banner upload failed" : activeTemplate.bannerImageUrl ? "Banner uploaded successfully" : ""}
              </div>
              <div className={`mail-aa-miniStatus ${attachmentUploadStatus || composer.attachments.length ? "is-visible" : ""}`}>
                {attachmentUploadStatus === "uploading" ? (
                  <>
                    <span className="mail-aa-miniBar"><span style={{width: `${Math.max(...Object.values(attachmentUploadProgress).map((value) => Number(value || 0)), 0)}%`}} /></span>
                    Attachment uploading
                  </>
                ) : attachmentUploadStatus === "failed" ? "Attachment upload failed" : composer.attachments.length ? "Attachment ready" : ""}
              </div>
            </div>

            {composer.attachments.length > 0 ? (
              <div className="mail-aa-attachmentGrid">
                {composer.attachments.map((item) => (
                  <article key={item.id} className="mail-aa-attachmentCard">
                    <div>
                      <div className="mail-aa-listTitle">{item.fileName}</div>
                      <div className="mail-aa-help">{formatBytes(item.size)} • {item.contentType}</div>
                    </div>
                    <button type="button" className="mail-aa-iconBtn" onClick={() => removeAttachment(item.id)}>
                      <FaTimes />
                    </button>
                  </article>
                ))}
              </div>
            ) : (
              <div className="mail-aa-empty mail-aa-emptyInline">
                <div className="mail-aa-emptyIcon"><FaPaperclip /></div>
                <p className="mail-aa-emptyCopy">No attachments added yet.</p>
              </div>
            )}
          </SectionCard>

          <SectionCard title="Actions" copy="Save the draft, send instantly, or queue a scheduled mail through the selected verified Gmail sender.">
            <div className="mail-aa-actions mail-aa-actionsWide">
              <button type="button" className="mail-aa-btn mail-aa-btnSecondary" onClick={() => handleSaveDraft("draft")} disabled={savingDraft || sendingMail}>
                <FaSave /> {savingDraft ? "Saving..." : "Save Draft"}
              </button>
              <button type="button" className="mail-aa-btn mail-aa-btnPrimary" onClick={handleSendNow} disabled={sendDisabled}>
                <FaPaperPlane /> {sendingMail ? "Queueing..." : "Send Now"}
              </button>
              <button type="button" className="mail-aa-btn mail-aa-btnPrimary" onClick={handleScheduleMail} disabled={scheduleDisabled}>
                <FaClock /> {schedulingMail ? "Scheduling..." : "Schedule Mail"}
              </button>
              <button type="button" className="mail-aa-btn mail-aa-btnGhost" onClick={handleConnectGmail}>
                <FaGoogle /> Reconnect Gmail
              </button>
            </div>
            {actionNotice.message ? (
              <div className={`mail-aa-actionNotice ${actionNotice.type === "error" ? "is-error" : "is-success"}`}>
                <span className="mail-aa-actionNoticeIcon">
                  {actionNotice.type === "error" ? <FaTimes /> : <FaCheckSquare />}
                </span>
                <span>{actionNotice.message}</span>
              </div>
            ) : null}
            <div className="mail-aa-help">
              Send Now and Schedule Mail both use the backend queue so large campaigns continue safely without browser-side bulk send blocking.
            </div>
          </SectionCard>
        </div>

        <div className="mail-aa-stack">
          <SectionCard title="Professional Preview" copy="The preview reflects the exact banner, layout, CTA, and attachment chips prepared for send.">
            <MailApnaArtistPreview
              composer={{
                ...composer,
                subject: mergedSubjectPreview,
                body: mergedBodyPreview,
                toText: mailMergePreviewLead.email || composer.toText,
              }}
              template={activeTemplate}
              compact
            />
            <div className="mail-aa-help">
              Preview merge sample: <strong>{mailMergePreviewLead.clientName || "No lead selected"}</strong> {mailMergePreviewLead.email ? `(${mailMergePreviewLead.email})` : ""}
            </div>
          </SectionCard>
        </div>
      </div>
    );
  }

  function renderManageTab() {
    if (manageCards.length === 0) {
      return (
        <div className="mail-aa-empty mail-aa-card">
          <div className="mail-aa-emptyIcon"><FaEnvelopeOpenText /></div>
          <h3 className="mail-aa-cardTitle">No mails saved yet</h3>
          <p className="mail-aa-emptyCopy">Save a draft or send a real message to populate this workspace.</p>
        </div>
      );
    }

    return (
      <div className="mail-aa-stack">
        {manageCards.map((item) => {
          const replies = getMailReplies(item);
          const expanded = expandedMailId === item.id;
          const replyState = String(item.replyTrackingState || "reply_pending").replace(/_/g, " ");
          return (
            <article key={item.id} className={`mail-aa-mailCard mail-aa-card ${expanded ? "is-open" : ""}`}>
              <button type="button" className="mail-aa-mailCardTop" onClick={() => setExpandedMailId(expanded ? "" : item.id)}>
                <span className="mail-aa-mailAvatar"><FaEnvelopeOpenText /></span>
                <span className="mail-aa-mailTitleBlock">
                  <strong>{item.subject || "Untitled mail"}</strong>
                  <small>{item.sender?.fromEmail || item.fromEmail || "No sender selected"}</small>
                </span>
                <span className={statusClass(item.status || item.replyTrackingState)}>{String(item.status || "draft").replace(/_/g, " ")}</span>
              </button>

              <div className="mail-aa-mailMetaGrid">
                <span><FaUsers /> <strong>To</strong> {(item.to || item.recipients || []).join(", ") || "No recipients"}</span>
                <span><FaPaperclip /> <strong>Attachments</strong> {item.attachmentCount || (item.attachments || []).length || 0}</span>
                <span><FaSignal /> <strong>Updated</strong> {formatDate(item.updatedAt || item.sentAt || item.createdAt)}</span>
                <span><FaReply /> <strong>Reply</strong> {replyState}</span>
                <span><FaEye /> <strong>Seen</strong> {item.openedAt || item.firstOpenedAt ? "Seen" : "Not seen"}</span>
                <span><FaClock /> <strong>Opened</strong> {formatDate(item.lastOpenedAt || item.openedAt || item.firstOpenedAt)}</span>
              </div>

              {item.failedReason ? <div className="mail-aa-help mail-aa-danger">{item.failedReason}</div> : null}

              {expanded ? (
                <div className="mail-aa-threadPanel">
                  <div className="mail-aa-threadHead">
                    <div>
                      <div className="mail-aa-inlineTitle">Live reply thread</div>
                      <div className="mail-aa-help">Realtime from stored mail reply data for this message.</div>
                    </div>
                    <span className={statusClass(replies.length ? "replied" : item.replyTrackingState || "reply_pending")}>
                      {replies.length ? `${replies.length} repl${replies.length > 1 ? "ies" : "y"}` : "No synced replies"}
                    </span>
                  </div>

                  <div className="mail-aa-threadTimeline">
                    <div className="mail-aa-threadBubble is-outgoing">
                      <span className="mail-aa-threadDot"><FaPaperPlane /></span>
                      <div>
                        <strong>Sent mail</strong>
                        <p>{String(item.body || "").slice(0, 180) || "Mail body saved in this campaign."}</p>
                      </div>
                    </div>
                    {replies.length ? replies.map((reply) => (
                      <div key={reply.id} className="mail-aa-threadBubble is-incoming">
                        <span className="mail-aa-threadDot"><FaReply /></span>
                        <div>
                          <strong>{reply.from}</strong>
                          <p>{reply.body}</p>
                          {reply.createdAt ? <small>{formatDate(reply.createdAt)}</small> : null}
                        </div>
                      </div>
                    )) : (
                      <div className="mail-aa-threadEmpty">
                        <FaReply /> Client replies are not synced yet for this thread.
                      </div>
                    )}
                  </div>

                  <div className="mail-aa-replyComposer">
                    <textarea
                      className="mail-aa-textarea"
                      rows="4"
                      value={replyDrafts[item.id] || ""}
                      onChange={(event) => setReplyDrafts((prev) => ({...prev, [item.id]: event.target.value}))}
                      placeholder="Write a premium reply..."
                    />
                    <div className="mail-aa-actions">
                      <button type="button" className="mail-aa-btn mail-aa-btnPrimary" onClick={() => prepareReply(item)}>
                        <FaReply /> Reply in composer
                      </button>
                      <button type="button" className="mail-aa-btn mail-aa-btnSecondary" onClick={() => loadMessageToComposer(item)}>
                        <FaChevronRight /> Open mail
                      </button>
                    </div>
                  </div>
                </div>
              ) : null}
            </article>
          );
        })}
      </div>
    );
  }

  function renderMonitorTab() {
    return (
      <div className="mail-aa-stack">
        <SectionCard title="Live Tracking Snapshot" copy="Real sent-mail opens, seen time, and reply state from stored message data.">
          <div className="mail-aa-metricList">
            {monitorCards.map((item) => (
              <div key={item.label} className="mail-aa-metricRow">
                <div className="mail-aa-inlineRow">
                  <span className="mail-aa-badge">{item.icon} {item.label}</span>
                </div>
                <div className="mail-aa-metricValue">{item.value}</div>
              </div>
            ))}
          </div>
        </SectionCard>

        <SectionCard title="Seen Mail Cards" copy="Each sent mail appears as a live tracking card with open time, recipient, and current state.">
          {trackingCards.length > 0 ? (
            <div className="mail-aa-list">
              {trackingCards.map((item) => {
                const seen = Boolean(item.openedAt || item.firstOpenedAt || item.openTrackingState === "seen" || Number(item.openCount || 0) > 0);
                return (
                  <article key={item.id} className="mail-aa-trackCard">
                    <div className="mail-aa-trackHead">
                      <span className="mail-aa-trackIcon"><FaEnvelopeOpenText /></span>
                      <div className="mail-aa-trackTitleBlock">
                        <strong>{item.subject || "Untitled mail"}</strong>
                        <small>{item.sender?.fromEmail || item.fromEmail || "No sender selected"}</small>
                      </div>
                      <span className={statusClass(seen ? "seen" : "unseen")}>
                        {seen ? "Seen" : "Not seen"}
                      </span>
                    </div>
                    <div className="mail-aa-trackMeta">
                      <span><FaUsers /> To: {(item.to || item.recipients || []).join(", ") || "No recipients"}</span>
                      <span><FaClock /> Opened: {formatDate(item.lastOpenedAt || item.openedAt || item.firstOpenedAt)}</span>
                      <span><FaEye /> Opens: {Number(item.openCount || 0)}</span>
                      <span><FaReply /> Reply: {String(item.replyTrackingState || "reply_pending").replace(/_/g, " ")}</span>
                    </div>
                    <div className="mail-aa-trackBody">
                      {seen ? "Client opened this mail." : "This mail has not been opened yet."}
                    </div>
                  </article>
                );
              })}
            </div>
          ) : (
            <div className="mail-aa-empty mail-aa-emptyInline">
              <div className="mail-aa-emptyIcon"><FaEye /></div>
              <p className="mail-aa-emptyCopy">No sent mail is available for open tracking yet.</p>
            </div>
          )}
        </SectionCard>
      </div>
    );
  }

  function renderTemplateTab() {
    return (
      <div className="mail-aa-stack">
        <SectionCard
          title="Template Identity"
          copy="Premium ApnaArtist styling with a clear hero, controlled highlights, and a polished footer."
          aside={(
            <button
              type="button"
              className="mail-aa-btn mail-aa-btnSecondary"
              onClick={() => {
                setSelectedTemplateId("");
                setTemplateDraft(ensureMailApnaArtistTemplate());
              }}
            >
              <FaPlusCircle /> New template
            </button>
          )}
        >
          <div className="mail-aa-templateHeroGrid">
            <div className="mail-aa-templateHeroCard">
              <div className="mail-aa-composeTileHead">
                <span className="mail-aa-composeIcon"><FaLayerGroup /></span>
                <div>
                  <div className="mail-aa-inlineTitle">Template name</div>
                  <div className="mail-aa-help">Name the design system used across saved mails.</div>
                </div>
              </div>
              <input className="mail-aa-input" value={templateDraft.templateName} onChange={(event) => setTemplateDraft((prev) => ({...prev, templateName: event.target.value}))} />
            </div>

            <label className="mail-aa-templateLogoCard">
              <div className="mail-aa-templateLogoThumb">
                <img
                  src={activeTemplate.logoUrl || "/brand/apnaartist-cream.png"}
                  alt="Template logo preview"
                />
              </div>
              <div className="mail-aa-templateLogoBody">
                <div className="mail-aa-composeTileHead">
                  <span className="mail-aa-composeIcon"><FaImage /></span>
                  <div>
                    <div className="mail-aa-inlineTitle">Template logo</div>
                    <div className="mail-aa-help">Upload a logo or paste a live URL.</div>
                  </div>
                </div>
                <label className="mail-aa-btn mail-aa-btnPrimary mail-aa-logoUploadBtn">
                  <FaUpload /> Upload logo
                  <input type="file" accept=".png,.jpg,.jpeg,.webp" hidden onChange={handleTemplateLogoUpload} />
                </label>
                <input
                  className="mail-aa-input"
                  value={templateDraft.logoUrl}
                  onChange={(event) => setTemplateDraft((prev) => ({...prev, logoUrl: event.target.value}))}
                  placeholder="Logo URL"
                />
              </div>
            </label>

            <div className="mail-aa-templateHeroCard">
              <div className="mail-aa-composeTileHead">
                <span className="mail-aa-composeIcon"><FaSignal /></span>
                <div>
                  <div className="mail-aa-inlineTitle">Brand color</div>
                  <div className="mail-aa-help">Keep hero and CTA colors aligned.</div>
                </div>
              </div>
              <div className="mail-aa-formSplit">
                <label className="mail-aa-field">
                  <span className="mail-aa-label">Primary</span>
                  <input className="mail-aa-input" value={templateDraft.primaryColor} onChange={(event) => setTemplateDraft((prev) => ({...prev, primaryColor: event.target.value}))} />
                </label>
                <label className="mail-aa-field">
                  <span className="mail-aa-label">Secondary</span>
                  <input className="mail-aa-input" value={templateDraft.secondaryColor} onChange={(event) => setTemplateDraft((prev) => ({...prev, secondaryColor: event.target.value}))} />
                </label>
              </div>
            </div>

            <div className="mail-aa-templateHeroCard">
              <div className="mail-aa-composeTileHead">
                <span className="mail-aa-composeIcon"><FaEdit /></span>
                <div>
                  <div className="mail-aa-inlineTitle">Hero copy</div>
                  <div className="mail-aa-help">Headline and supporting line for the email banner.</div>
                </div>
              </div>
              <label className="mail-aa-field">
                <span className="mail-aa-label">Banner title</span>
                <input className="mail-aa-input" value={templateDraft.bannerTitle} onChange={(event) => setTemplateDraft((prev) => ({...prev, bannerTitle: event.target.value}))} />
              </label>
              <label className="mail-aa-field">
                <span className="mail-aa-label">Banner subtitle</span>
                <textarea className="mail-aa-textarea" value={templateDraft.bannerSubtitle} onChange={(event) => setTemplateDraft((prev) => ({...prev, bannerSubtitle: event.target.value}))} />
              </label>
            </div>
          </div>
        </SectionCard>

        <SectionCard title="Template Details" copy="Control the visible hero image, highlight cards, footer language, and social line in one place.">
          <div className="mail-aa-templateDetailTop">
            <div className="mail-aa-templateMiniCard">
              <div className="mail-aa-composeTileHead">
                <span className="mail-aa-composeIcon"><FaImage /></span>
                <div>
                  <div className="mail-aa-inlineTitle">Banner</div>
                  <div className="mail-aa-help">Shown in the email hero and preview.</div>
                </div>
              </div>
              <input className="mail-aa-input" value={templateDraft.bannerImageUrl} onChange={(event) => setTemplateDraft((prev) => ({...prev, bannerImageUrl: event.target.value}))} />
              <input className="mail-aa-input" value={templateDraft.bannerAlt} onChange={(event) => setTemplateDraft((prev) => ({...prev, bannerAlt: event.target.value}))} placeholder="Banner alt text" />
            </div>

            {[0, 1, 2].map((index) => (
              <label key={index} className="mail-aa-templateMiniCard">
                <span className="mail-aa-label">Highlight {index + 1}</span>
                <input
                  className="mail-aa-input"
                  value={templateDraft.highlights[index] || ""}
                  onChange={(event) => setTemplateDraft((prev) => {
                    const highlights = [...(prev.highlights || [])];
                    highlights[index] = event.target.value;
                    return {...prev, highlights};
                  })}
                />
              </label>
            ))}
          </div>

          <label className="mail-aa-field mail-aa-templateMiniCard">
            <span className="mail-aa-label">Footer text</span>
            <textarea className="mail-aa-textarea" value={templateDraft.footerText} onChange={(event) => setTemplateDraft((prev) => ({...prev, footerText: event.target.value}))} />
          </label>

          <div className="mail-aa-formSplit">
            <label className="mail-aa-field mail-aa-templateMiniCard">
              <span className="mail-aa-label">Footer phone</span>
              <input className="mail-aa-input" value={templateDraft.footerPhone} onChange={(event) => setTemplateDraft((prev) => ({...prev, footerPhone: event.target.value}))} />
            </label>
            <label className="mail-aa-field mail-aa-templateMiniCard">
              <span className="mail-aa-label">Footer website</span>
              <input className="mail-aa-input" value={templateDraft.footerWebsite} onChange={(event) => setTemplateDraft((prev) => ({...prev, footerWebsite: event.target.value}))} />
            </label>
          </div>

          <div className="mail-aa-formSplit">
            <label className="mail-aa-field mail-aa-templateMiniCard">
              <span className="mail-aa-label">Footer address</span>
              <input className="mail-aa-input" value={templateDraft.footerAddress} onChange={(event) => setTemplateDraft((prev) => ({...prev, footerAddress: event.target.value}))} />
            </label>
            <label className="mail-aa-field mail-aa-templateMiniCard">
              <span className="mail-aa-label">Social line</span>
              <input className="mail-aa-input" value={templateDraft.socialLine} onChange={(event) => setTemplateDraft((prev) => ({...prev, socialLine: event.target.value}))} />
            </label>
          </div>

          <div className="mail-aa-toggleGrid mail-aa-toggleGridPremium">
            {[
              ["showLogo", "Show logo"],
              ["showBanner", "Show banner"],
              ["showCta", "Show CTA"],
              ["showFooter", "Show footer"],
              ["showHighlights", "Show highlights"],
            ].map(([key, label]) => (
              <label key={key} className="mail-aa-toggleWrap">
                <span>{label}</span>
                <input
                  type="checkbox"
                  checked={Boolean(templateDraft[key])}
                  onChange={(event) => setTemplateDraft((prev) => ({...prev, [key]: event.target.checked}))}
                />
              </label>
            ))}
          </div>
        </SectionCard>

        <SectionCard title="Template Preview" copy="Review the premium template frame before saving it to Firestore.">
          <MailApnaArtistPreview composer={composer} template={templateDraft} compact />
          <div className="mail-aa-actions">
            <button type="button" className="mail-aa-btn mail-aa-btnPrimary" onClick={handleSaveTemplate} disabled={savingTemplate}>
              <FaSave /> {savingTemplate ? "Saving Template..." : "Save Template"}
            </button>
          </div>
        </SectionCard>
      </div>
    );
  }

  function renderLeadsTab() {
    return (
      <div className="mail-aa-stack">
        <SectionCard title="Lead Manager" copy="Create and manage lead records in real time. These leads can be selected directly from the send flow.">
          <div className="mail-aa-formGrid">
            <label className="mail-aa-field">
              <span className="mail-aa-label">Client name</span>
              <input
                className="mail-aa-input"
                value={leadForm.clientName}
                onChange={(event) => setLeadForm((prev) => ({...prev, clientName: event.target.value}))}
                placeholder="Client name"
              />
            </label>
            <label className="mail-aa-field">
              <span className="mail-aa-label">Email</span>
              <input
                className="mail-aa-input"
                value={leadForm.email}
                onChange={(event) => setLeadForm((prev) => ({...prev, email: event.target.value}))}
                placeholder="client@example.com"
              />
            </label>
            <label className="mail-aa-field">
              <span className="mail-aa-label">Phone number</span>
              <input
                className="mail-aa-input"
                value={leadForm.phone}
                onChange={(event) => setLeadForm((prev) => ({...prev, phone: event.target.value}))}
                placeholder="+91 ..."
              />
            </label>
            <label className="mail-aa-field">
              <span className="mail-aa-label">Location</span>
              <input
                className="mail-aa-input"
                value={leadForm.location}
                onChange={(event) => setLeadForm((prev) => ({...prev, location: event.target.value}))}
                placeholder="Mumbai"
              />
            </label>
          </div>
          <div className="mail-aa-actions">
            <button type="button" className="mail-aa-btn mail-aa-btnPrimary" onClick={handleSaveLead} disabled={savingLead}>
              <FaSave /> {savingLead ? "Saving Lead..." : leadForm.id ? "Update Lead" : "Save Lead"}
            </button>
            <button type="button" className="mail-aa-btn mail-aa-btnSecondary" onClick={() => setLeadForm(ensureMailApnaArtistLead())}>
              <FaPlusCircle /> New Lead
            </button>
          </div>
        </SectionCard>

        <SectionCard title="Real-Time Leads Table" copy="Select leads for send, edit them, or delete them. Name and email are pulled automatically into the mail flow.">
          {leads.length > 0 ? (
            <div className="mail-aa-list">
              {leads.map((lead) => {
                const selected = selectedLeadIds.includes(lead.id);
                return (
                  <article key={lead.id} className="mail-aa-listCard">
                    <div className="mail-aa-listTop">
                      <div>
                        <div className="mail-aa-listTitle">{lead.clientName || "Unnamed lead"}</div>
                        <div className="mail-aa-help">{lead.email}</div>
                      </div>
                      <span className={statusClass(selected ? "ready" : "template")}>{selected ? "Selected" : "Saved"}</span>
                    </div>
                    <div className="mail-aa-listMeta">
                      <span><strong>Phone:</strong> {lead.phone || "-"}</span>
                      <span><strong>Location:</strong> {lead.location || "-"}</span>
                    </div>
                    <div className="mail-aa-actions">
                      <button type="button" className="mail-aa-btn mail-aa-btnSecondary" onClick={() => toggleLeadSelection(lead.id)}>
                        <FaCheckSquare /> {selected ? "Unselect" : "Select"}
                      </button>
                      <button type="button" className="mail-aa-btn mail-aa-btnSecondary" onClick={() => loadLeadIntoForm(lead)}>
                        <FaEdit /> Edit
                      </button>
                      <button type="button" className="mail-aa-btn mail-aa-btnGhost" onClick={() => handleDeleteLead(lead.id)}>
                        <FaTrash /> Delete
                      </button>
                    </div>
                  </article>
                );
              })}
            </div>
          ) : (
            <div className="mail-aa-empty mail-aa-card">
              <div className="mail-aa-emptyIcon"><FaUsers /></div>
              <h3 className="mail-aa-cardTitle">No leads yet</h3>
              <p className="mail-aa-emptyCopy">Add a client name, email, phone, and location to start using real-time leads.</p>
            </div>
          )}
        </SectionCard>
      </div>
    );
  }

  return (
    <div className="mail-aa-page">
      <section className="mail-aa-header mail-aa-card">
        <div className="mail-aa-headerTop">
          <div className="mail-aa-brandWrap">
            <img className="mail-aa-headerLogo" src="/brand/apnaartist-cream.png" alt="ApnaArtist" />
            <div>
              <div className="mail-aa-eyebrow"><FaLayerGroup /> Mail ApnaArtist</div>
              <h1 className="mail-aa-title">ApnaArtist Mail Studio</h1>
            </div>
          </div>
        </div>
        <div className="mail-aa-statsRow">
          {headerStats.map((item) => (
            <div key={item.label} className="mail-aa-statChip">
              <span className="mail-aa-statLabel">{item.label}</span>
              <strong className="mail-aa-statNumber">{item.value}</strong>
            </div>
          ))}
        </div>
      </section>

      <section className="mail-aa-tabBar mail-aa-card">
        <div className="mail-aa-tabList">
          {MAIL_APNAARTIST_TABS.map((item) => (
            <button
              key={item.key}
              type="button"
              className={`mail-aa-tab ${activeTab === item.key ? "is-active" : ""}`}
              onClick={() => setActiveTab(item.key)}
            >
              {item.label}
            </button>
          ))}
        </div>
      </section>

      {activeTab === "send" ? renderSendTab() : null}
      {activeTab === "manage" ? renderManageTab() : null}
      {activeTab === "monitor" ? renderMonitorTab() : null}
      {activeTab === "template" ? renderTemplateTab() : null}
      {activeTab === "leads" ? renderLeadsTab() : null}

      <ToastContainer position="top-right" autoClose={2600} />
    </div>
  );
}
